<script setup lang="ts">
import { computed, reactive, ref, onUnmounted, nextTick } from 'vue';
import { useRouter } from 'vue-router';
import { useDetectionStore } from '@/modules/detection';
import { formatRelativeTime } from '@/utils/time';
import { clipToWavBlob } from '@/utils/audioExport';
import { triggerDownload } from '@/utils/download';
import {
  buildDetectionReport,
  detectionFilenameBase,
  detectionReportText,
  type DetectionReport,
} from '@/utils/detectionReport';
import {
  EXPLOSION_CLASS_COLORS,
  EXPLOSION_CLASS_LABELS,
  EXPLOSION_CLASS_ICONS,
} from '@/types/geolocation';
import type { DetectionEvent, EventClassName } from '@/types';

/**
 * Лента собственных детекций устройства (то, что поймал микрофон и отправил на сервер) —
 * в отличие от EventFeed, который показывает события, пришедшие с сервера по региону.
 * Источник — detectionStore (IndexedDB). Показывает статус отправки, онсет/уверенность,
 * и по кнопке «Подробнее» — вероятности 3 каскадов, вероятности всех классов и
 * 9-секундную аудиозапись (3 с до … 3 с после), которую можно прослушать.
 *
 * Под плеером, если клип доступен, есть две кнопки:
 *   • «Сохранить в WAV» — скачать клип как .wav (через clipToWavBlob → triggerDownload).
 *   • «Отправить для анализа» — загрузить клип (как WAV) на сервер; делегируется родителю
 *     эмитом send-audio, как retry-send. Авто-захваченный клип по умолчанию локальный —
 *     отправка это явное ручное действие пользователя.
 */

const emit = defineEmits<{
  (e: 'retry-send', id: number): void;
  (e: 'send-audio', payload: { id: number; blob: Blob; report: DetectionReport }): void;
}>();

const store = useDetectionStore();
const router = useRouter();

/** Newest first; cap the rendered list so a long local log can't bloat the DOM. */
const events = computed(() => store.events.slice(0, 50));

/** Canonical class order — matches the model's classProbabilities vector layout. */
const CLASS_ORDER: EventClassName[] = ['none', 'geran', 'pushka', 'roked', 'shots'];

/** Stable per-card key (id when present, else the creation timestamp). */
function keyOf(e: DetectionEvent): number {
  return e.id ?? e.createdAt;
}

/** Onset offset within the detection window (ms), when the model exposed an onset head. */
function onsetOffsetMs(e: DetectionEvent): number | null {
  if (e.onsetTime === null) return null;
  return Math.round(e.onsetTime - e.timestamp);
}

function pct(v: number | null | undefined): string {
  return v === null || v === undefined ? '—' : `${Math.round(v * 100)}%`;
}

// ── Diagnostics formatting helpers (the new «Подробнее» sections) ─────────────────────────
// All purely presentational; they read the deep-diagnostic block attached to the event.

/** Fixed-precision number with unit, e.g. `0.0123` → "0.012"; null/undefined → "—". */
function fnum(v: number | null | undefined, digits = 3): string {
  if (v === null || v === undefined || !Number.isFinite(v)) return '—';
  return v.toFixed(digits);
}
/** Frequency → human (Hz / kHz). */
function freq(v: number | null | undefined): string {
  if (v === null || v === undefined || !Number.isFinite(v) || v <= 0) return '—';
  return v >= 1000 ? `${(v / 1000).toFixed(2)} кГц` : `${v.toFixed(0)} Гц`;
}
/** Milliseconds → human (ms / s). */
function ms(v: number | null | undefined): string {
  if (v === null || v === undefined || !Number.isFinite(v)) return '—';
  return v >= 1000 ? `${(v / 1000).toFixed(2)} с` : `${Math.round(v)} мс`;
}
/** Map a Stage-0 reason code → short Russian label. */
const REASON_LABELS: Record<string, string> = {
  rms_energy: 'энергия',
  spectral_flux: 'резкость',
  low_frequency: 'низкие частоты',
};
function reasonLabel(r: string): string {
  return REASON_LABELS[r] ?? r;
}
/** Doppler full swing = 2·shiftHz, in Hz. */
function dopplerSwingHz(d: { shiftHz: number | null }): string {
  if (d.shiftHz === null || !Number.isFinite(d.shiftHz)) return '—';
  const swing = 2 * d.shiftHz;
  return swing >= 1000 ? `${(swing / 1000).toFixed(2)} кГц` : `${swing.toFixed(1)} Гц`;
}
/** Estimated Doppler speed m/s → km/h for readability. */
function speedKmh(v: number | null | undefined): string {
  if (v === null || v === undefined || !Number.isFinite(v)) return '—';
  return `${Math.round(v * 3.6)} км/ч`;
}

/** Draw the raw onset curve (per-frame onset probability) into a canvas element. */
function drawOnsetCurve(canvas: HTMLCanvasElement, curve: number[]): void {
  const ctx = canvas.getContext('2d');
  if (!ctx) return;
  const w = canvas.width;
  const h = canvas.height;
  ctx.clearRect(0, 0, w, h);
  if (curve.length === 0) return;
  const max = Math.max(...curve, 0.01);
  // Baseline.
  ctx.strokeStyle = '#232d40';
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.moveTo(0, h - 1);
  ctx.lineTo(w, h - 1);
  ctx.stroke();
  // Area fill.
  ctx.beginPath();
  for (let i = 0; i < curve.length; i++) {
    const x = (i / (curve.length - 1)) * w;
    const y = h - (curve[i] / max) * (h - 2) - 1;
    if (i === 0) ctx.moveTo(x, y);
    else ctx.lineTo(x, y);
  }
  ctx.lineTo(w, h);
  ctx.lineTo(0, h);
  ctx.closePath();
  ctx.fillStyle = 'rgba(42, 171, 238, 0.18)';
  ctx.fill();
  // Stroke the top edge on top of the fill.
  ctx.beginPath();
  for (let i = 0; i < curve.length; i++) {
    const x = (i / (curve.length - 1)) * w;
    const y = h - (curve[i] / max) * (h - 2) - 1;
    if (i === 0) ctx.moveTo(x, y);
    else ctx.lineTo(x, y);
  }
  ctx.strokeStyle = '#2aabee';
  ctx.lineWidth = 1.5;
  ctx.stroke();
}

/** Draw the raw tonal time-series (freq × time scatter) for a fly-by Doppler fit. */
function drawTonalScatter(
  canvas: HTMLCanvasElement,
  samples: { timeMs: number; freqHz: number; weight: number }[],
): void {
  const ctx = canvas.getContext('2d');
  if (!ctx) return;
  const w = canvas.width;
  const h = canvas.height;
  ctx.clearRect(0, 0, w, h);
  if (samples.length === 0) return;
  const tMin = Math.min(...samples.map((s) => s.timeMs));
  const tMax = Math.max(...samples.map((s) => s.timeMs));
  const fMin = Math.min(...samples.map((s) => s.freqHz));
  const fMax = Math.max(...samples.map((s) => s.freqHz));
  const tSpan = tMax - tMin || 1;
  const fSpan = fMax - fMin || 1;
  const maxW = Math.max(...samples.map((s) => s.weight), 0.001);
  for (const s of samples) {
    const x = ((s.timeMs - tMin) / tSpan) * (w - 4) + 2;
    const y = h - ((s.freqHz - fMin) / fSpan) * (h - 4) - 2;
    const r = 1 + (s.weight / maxW) * 2.5;
    const alpha = 0.25 + (s.weight / maxW) * 0.6;
    ctx.beginPath();
    ctx.arc(x, y, r, 0, Math.PI * 2);
    ctx.fillStyle = `rgba(202, 138, 255, ${alpha})`; // purple — distinct from onset blue
    ctx.fill();
  }
}

/** Class probabilities paired with their class, in canonical order (ignores trailing extras). */
function classProbs(e: DetectionEvent): { cls: EventClassName; p: number }[] {
  return (e.classProbabilities ?? []).map((p, i) => ({
    cls: CLASS_ORDER[i] ?? 'none',
    p,
  }));
}

/**
 * Effective sync status, normalising legacy events (no syncStatus field) via the boolean
 * synced flag so old cards still render correctly.
 */
type SyncStatus = 'pending' | 'sent' | 'no_location' | 'error';
function syncStatusOf(e: DetectionEvent): SyncStatus {
  return (e.syncStatus as SyncStatus | undefined) ?? (e.synced ? 'sent' : 'pending');
}

/** Header icon for the sync state (mirrors the status section text). */
function syncIcon(e: DetectionEvent): string {
  switch (syncStatusOf(e)) {
    case 'sent':
      return '✅';
    case 'error':
    case 'no_location':
      return '⚠';
    default:
      return '⏳';
  }
}

function syncTitle(e: DetectionEvent): string {
  switch (syncStatusOf(e)) {
    case 'sent':
      return 'Отправлено на сервер';
    case 'error':
      return 'Ошибка отправки';
    case 'no_location':
      return 'Не отправлено: нет координат';
    default:
      return 'Отправка…';
  }
}

// ── Expandable "Подробнее" panel + lazily-loaded audio clip ───────────────────────────────
const expanded = reactive(new Set<number>());
/**
 * Per-event audio state: object URL for playback (or null = no clip), the raw Blob itself (kept
 * so the save/upload actions don't re-fetch from IDB), and whether we've queried.
 */
const audio = reactive<Record<number, { url: string | null; blob: Blob | null; loaded: boolean }>>({});
/** Per-event retry state — disables the "Повторить" button while a retry is in flight. */
const retrying = reactive(new Set<number>());
/** Transient confirmation shown after a retry succeeds. */
const retriedOk = ref<Set<number>>(new Set());
/** Per-event «Сохранить в WAV» state — disables the button while the WAV re-encode runs. */
const savingWav = reactive(new Set<number>());
/** Per-event «Отправить для анализа» state — id of the event currently uploading (gates button). */
const audioSending = reactive(new Set<number>());
/** Transient confirmation shown after an audio upload succeeds. */
const audioSentOk = ref<Set<number>>(new Set());
/** Per-event upload error message (cleared on retry). */
const audioSendError = reactive(new Map<number, string>());

async function toggle(e: DetectionEvent): Promise<void> {
  const k = keyOf(e);
  if (expanded.has(k)) {
    expanded.delete(k);
    return;
  }
  expanded.add(k);
  // Lazily fetch the clip the first time the card is opened. Clips are kept only for the
  // 5 newest detections / last hour, so older cards resolve to null (→ "недоступна").
  if (e.id !== undefined && !audio[k]?.loaded) {
    const blob = await store.getAudioClip(e.id);
    audio[k] = { url: blob ? URL.createObjectURL(blob) : null, blob, loaded: true };
  }
  // Draw the diagnostic canvases (onset curve, tonal scatter) now that the panel is in the DOM.
  // Each canvas is keyed by event id + purpose so multiple cards don't collide.
  await nextTick();
  drawDiagCanvases(k, e);
}

/** Render the onset-curve and tonal-scatter canvases for a just-expanded card. */
function drawDiagCanvases(k: number, e: DetectionEvent): void {
  const onset = e.diagnostics?.onset;
  if (onset && onset.curve.length > 0) {
    const c = document.getElementById(`onset-cv-${k}`) as HTMLCanvasElement | null;
    if (c) drawOnsetCurve(c, onset.curve);
  }
  const doppler = e.diagnostics?.doppler;
  if (doppler && doppler.tonalSamples.length > 0) {
    const c = document.getElementById(`tonal-cv-${k}`) as HTMLCanvasElement | null;
    if (c) drawTonalScatter(c, doppler.tonalSamples);
  }
}

async function retrySend(e: DetectionEvent): Promise<void> {
  const id = e.id;
  if (id === undefined) return;
  retrying.add(id);
  try {
    emit('retry-send', id);
    // The parent updates the store reactively (async POST); re-check the status after a tick
    // to surface the outcome. The `retrying` set just gates the button against double-clicks.
    await new Promise<void>((resolve) => setTimeout(resolve, 600));
    const updated = store.events.find((x) => x.id === id);
    if (updated && syncStatusOf(updated) === 'sent') {
      retriedOk.value.add(id);
      setTimeout(() => retriedOk.value.delete(id), 2500);
    }
  } finally {
    retrying.delete(id);
  }
}

function remove(e: DetectionEvent): void {
  if (e.id !== undefined) void store.deleteEvent(e.id);
}

/**
 * Download the clip for an event as a .wav file, plus a companion .txt carrying the full
 * "how it was detected" reasoning (cascade stages, class probabilities, all diagnostics, audio
 * meta, embedding). The WAV is re-encoded to 16-bit PCM so the user always gets a portable file
 * regardless of how the clip was captured. Falls back to re-reading the clip from IDB if the
 * in-memory blob was pruned. The two files share a base filename so they stay paired on disk.
 */
/**
 * Open the per-frame debug graphs page for this event — re-analyses the 9 s clip through the
 * cascade pipeline (3 s windows, 1 s hop) and plots Stage 0/1/2 probability-vs-time. The page
 * handles lazy model loading itself, so we just navigate by event id.
 */
function openDebugGraphs(e: DetectionEvent): void {
  if (e.id === undefined) return;
  void router.push({ name: 'radar-debug', params: { id: e.id } });
}

async function downloadWav(e: DetectionEvent): Promise<void> {
  if (e.id === undefined) return;
  const k = keyOf(e);
  savingWav.add(k);
  try {
    let blob = audio[k]?.blob;
    if (!blob) {
      blob = await store.getAudioClip(e.id);
      if (!blob) return; // clip pruned/expired — nothing to save
      audio[k] = { ...(audio[k] ?? { url: null, blob: null, loaded: true }), blob };
    }
    const base = detectionFilenameBase(e);
    const wav = await clipToWavBlob(blob);
    triggerDownload(wav, `${base}.wav`);
    // Companion report: the full reasoning trace, human-readable.
    const reportTxt = detectionReportText(e);
    triggerDownload(new Blob([reportTxt], { type: 'text/plain;charset=utf-8' }), `${base}.txt`);
  } catch (err) {
    console.error('[MyEventsFeed] WAV save failed:', err);
  } finally {
    savingWav.delete(k);
  }
}

/**
 * Upload the clip for an event (as WAV) plus the full "how it was detected" report to the server
 * for analysis. Delegates the network call to the parent via the `send-audio` emit — the parent
 * wires it to useLiveDetection, exactly like `retry-send`. The parent reports the outcome back via
 * `markAudioSent` / `markAudioSendError` (exposed below), which flip the per-event button state.
 */
async function uploadAudio(e: DetectionEvent): Promise<void> {
  if (e.id === undefined) return;
  const id = e.id;
  if (audioSending.has(id)) return;
  const k = keyOf(e);
  audioSending.add(id);
  audioSendError.delete(id);
  try {
    let blob = audio[k]?.blob;
    if (!blob) {
      blob = await store.getAudioClip(id);
      if (!blob) {
        // Clip pruned/expired — fail immediately without involving the parent.
        audioSending.delete(id);
        audioSendError.set(id, 'Запись недоступна');
        return;
      }
      audio[k] = { ...(audio[k] ?? { url: null, blob: null, loaded: true }), blob };
    }
    // Always send a WAV so the backend handles a single format.
    const wav = await clipToWavBlob(blob);
    // The full reasoning trace travels alongside the audio so the server has everything needed
    // to re-analyse: cascade stages, class probabilities, all diagnostics, audio meta, embedding.
    const report = buildDetectionReport(e);
    emit('send-audio', { id, blob: wav, report });
    // Outcome is reported by the parent via markAudioSent / markAudioSendError.
  } catch (err) {
    audioSending.delete(id);
    audioSendError.set(id, err instanceof Error ? err.message : 'Ошибка отправки');
  }
}

/** Parent reports a successful upload — clears the spinner, shows a transient confirmation. */
function markAudioSent(eventId: number): void {
  audioSending.delete(eventId);
  audioSendError.delete(eventId);
  audioSentOk.value.add(eventId);
  setTimeout(() => audioSentOk.value.delete(eventId), 2500);
}

/** Parent reports a failed upload — clears the spinner, surfaces the error message. */
function markAudioSendError(eventId: number, message: string): void {
  audioSending.delete(eventId);
  audioSendError.set(eventId, message);
}

// Expose the outcome hooks so the parent can call them via a template ref.
defineExpose({ markAudioSent, markAudioSendError });

// Free any object URLs we created when the feed is torn down.
onUnmounted(() => {
  for (const k of Object.keys(audio)) {
    const u = audio[Number(k)]?.url;
    if (u) URL.revokeObjectURL(u);
  }
});
</script>

<template>
  <section class="feed">
    <div
      v-if="events.length === 0"
      class="empty"
    >
      Пока нет собственных детекций. Включите мониторинг — пойманные события появятся здесь.
    </div>

    <div
      v-for="e in events"
      :key="keyOf(e)"
      class="card"
    >
      <div class="card-row">
        <!-- Класс — иконкой (как у чипа микрофона), цвет в тон класса -->
        <span
          class="cls-icon"
          :style="{ color: EXPLOSION_CLASS_COLORS[e.eventClass] }"
          :title="EXPLOSION_CLASS_LABELS[e.eventClass] || e.eventClass"
        >{{ EXPLOSION_CLASS_ICONS[e.eventClass] }}</span>

        <div class="card-body">
          <div class="card-title">
            <span class="card-conf">{{ (e.confidence * 100).toFixed(0) }}%</span>
            <!-- Статус отправки — иконкой: ✅ отправлено / ⏳ ожидает / ⚠ не ушло (см. «Подробнее»). -->
            <span
              class="sync-icon"
              :class="{ synced: syncStatusOf(e) === 'sent' }"
              :title="syncTitle(e)"
            >{{ syncIcon(e) }}</span>
          </div>
          <div class="card-time">
            {{ formatRelativeTime(e.createdAt) }}
          </div>
        </div>

        <!-- Подробнее — иконка ℹ + шеврон, разворачивает панель -->
        <button
          class="info-btn"
          :class="{ open: expanded.has(keyOf(e)) }"
          title="Подробнее"
          @click="toggle(e)"
        >
          ℹ<span class="chev">▾</span>
        </button>
        <button
          class="del-btn"
          title="Удалить из локального журнала"
          @click="remove(e)"
        >
          ✕
        </button>
      </div>

      <!-- Детали: каскады, вероятности классов, аудиозапись -->
      <div
        v-if="expanded.has(keyOf(e))"
        class="details"
      >
        <div class="det-section-title">
          Каскады
          <span
            v-if="e.isMovingPass"
            class="agg-tag"
            title="Пролётная цель: каскады ① и ③ агрегированы (максимум) по всем окнам пролёта, итог ② — пиковое окно"
          >пролёт · агрегировано</span>
        </div>
        <div class="stage-grid">
          <div class="stage-row">
            <span class="stage-num">①</span>
            <span class="stage-name">Фильтр аномалий</span>
            <span class="stage-val">{{ pct(e.cascade?.stage1Confidence) }}</span>
          </div>
          <div class="stage-row">
            <span class="stage-num">②</span>
            <span class="stage-name">Классификатор</span>
            <span class="stage-val">{{ pct(e.cascade?.stage2Confidence) }}</span>
          </div>
          <div class="stage-row">
            <span class="stage-num">③</span>
            <span class="stage-name">Проверка</span>
            <span class="stage-val">{{ pct(e.cascade?.stage3VerificationScore) }}</span>
          </div>
        </div>

        <!-- Статус отправки на сервер с причиной и кнопкой повтора -->
        <div class="det-section-title">
          Статус отправки
        </div>
        <div
          class="sync-status"
          :class="syncStatusOf(e)"
        >
          <template v-if="syncStatusOf(e) === 'sent'">
            ✅ Отправлено на сервер
          </template>
          <template v-else-if="syncStatusOf(e) === 'pending'">
            ⏳ Отправка…
          </template>
          <template v-else>
            <div class="sync-status-head">
              ⚠ {{ syncStatusOf(e) === 'no_location' ? 'Не отправлено: координаты не установлены' : 'Ошибка отправки' }}
            </div>
            <div
              v-if="e.syncError"
              class="sync-status-reason"
            >
              {{ e.syncError }}
            </div>
            <div
              v-if="syncStatusOf(e) === 'no_location'"
              class="sync-status-hint"
            >
              Укажите точку на карте (тап по карте) или включите GPS, затем нажмите «Повторить».
            </div>
            <div
              v-if="retriedOk.has(e.id!) && syncStatusOf(e) === 'sent'"
              class="sync-status-ok"
            >
              ✓ Ушло на сервер
            </div>
            <button
              v-if="e.id !== undefined"
              class="retry-btn"
              :disabled="retrying.has(e.id)"
              @click="retrySend(e)"
            >
              {{ retrying.has(e.id) ? 'Отправка…' : '↻ Повторить отправку' }}
            </button>
          </template>
        </div>

        <div class="det-section-title">
          Вероятности классов
        </div>
        <div class="prob-grid">
          <div
            v-for="row in classProbs(e)"
            :key="row.cls"
            class="prob-row"
            :class="{ top: row.cls === e.eventClass }"
          >
            <span
              class="prob-icon"
              :style="{ color: EXPLOSION_CLASS_COLORS[row.cls] }"
              :title="EXPLOSION_CLASS_LABELS[row.cls] || row.cls"
            >{{ EXPLOSION_CLASS_ICONS[row.cls] }}</span>
            <span class="prob-bar">
              <span
                class="prob-fill"
                :style="{ width: `${Math.round(row.p * 100)}%`, background: EXPLOSION_CLASS_COLORS[row.cls] }"
              />
            </span>
            <span class="prob-val">{{ (row.p * 100).toFixed(0) }}%</span>
          </div>
        </div>

        <!-- ── Глубокая диагностика (новое): Stage 0, onset, доплер, пролёт ── -->

        <!-- Stage 0: энергетический фильтр — фичи, по которым окно пустили дальше -->
        <template v-if="e.diagnostics?.stage0Energy">
          <div class="det-section-title">
            Фильтр энергии · Stage 0
            <span class="diag-tag">почему окно пустили дальше</span>
          </div>
          <div class="diag-grid">
            <div class="diag-row">
              <span class="diag-name">RMS энергия</span>
              <span class="diag-val">{{ fnum(e.diagnostics.stage0Energy.rms) }}</span>
            </div>
            <div class="diag-row">
              <span class="diag-name">Spectral flux</span>
              <span class="diag-val">{{ fnum(e.diagnostics.stage0Energy.flux) }}</span>
            </div>
            <div class="diag-row">
              <span class="diag-name">Zero-crossing rate</span>
              <span class="diag-val">{{ fnum(e.diagnostics.stage0Energy.zcr) }}</span>
            </div>
            <div class="diag-row">
              <span class="diag-name">Доля низких частот</span>
              <span class="diag-val">{{ pct(e.diagnostics.stage0Energy.lowBandEnergyRatio) }}</span>
            </div>
            <div class="diag-row">
              <span class="diag-name">Шумовой порог (EMA)</span>
              <span class="diag-val">{{ fnum(e.diagnostics.stage0Energy.noiseFloor) }}</span>
            </div>
            <div class="diag-row">
              <span class="diag-name">Спектральный центроид</span>
              <span class="diag-val">{{ freq(e.diagnostics.stage0Energy.spectralCentroid) }}</span>
            </div>
          </div>
          <div class="reason-chips">
            <span
              v-for="r in e.diagnostics.stage0Energy.reason"
              :key="r"
              class="reason-chip"
            >✓ {{ reasonLabel(r) }}</span>
            <span
              v-if="e.diagnostics.stage0Energy.reason.length === 0"
              class="reason-chip muted"
            >ни одна из 3 проверок</span>
          </div>
        </template>

        <!-- Onset: пиковая кривая вероятности onset-head -->
        <template v-if="e.diagnostics?.onset">
          <div class="det-section-title">
            Онсет · точность срабатывания
            <span class="diag-tag">sub-frame пик onset-head</span>
          </div>
          <div class="diag-grid">
            <div class="diag-row">
              <span class="diag-name">Пик onset-кривой</span>
              <span class="diag-val">{{ pct(e.diagnostics.onset.peakValue) }}</span>
            </div>
            <div class="diag-row">
              <span class="diag-name">Позиция пика (кадр)</span>
              <span class="diag-val">{{ fnum(e.diagnostics.onset.onsetFrame, 2) }}</span>
            </div>
          </div>
          <canvas
            :id="`onset-cv-${keyOf(e)}`"
            class="diag-canvas"
            width="320"
            height="56"
          />
          <div class="diag-hint">
            per-frame вероятность onset ({{ e.diagnostics.onset.curve.length }} кадров)
          </div>
        </template>

        <!-- Пролёт: геометрия агрегации окон в одно событие (только для isMovingPass) -->
        <template v-if="e.diagnostics?.pass">
          <div class="det-section-title">
            Пролёт · агрегация
            <span class="diag-tag">{{ e.diagnostics.pass.onsetSource === 'doppler' ? 'тайминг — Doppler-CPA' : 'тайминг — энергетический центроид' }}</span>
          </div>
          <div class="diag-grid">
            <div class="diag-row">
              <span class="diag-name">Энергетический центроид</span>
              <span class="diag-val">{{ ms(e.diagnostics.pass.centroidMs) }}</span>
            </div>
            <div class="diag-row">
              <span class="diag-name">Разброс σ (неопр. тайминга)</span>
              <span class="diag-val">{{ ms(e.diagnostics.pass.sigmaMs) }}</span>
            </div>
            <div class="diag-row">
              <span class="diag-name">Окон в пролёте</span>
              <span class="diag-val">{{ e.diagnostics.pass.windowCount }}</span>
            </div>
            <div class="diag-row">
              <span class="diag-name">Длительность пролёта</span>
              <span class="diag-val">{{ ms(e.diagnostics.pass.spanEndMs - e.diagnostics.pass.spanStartMs) }}</span>
            </div>
            <div class="diag-row">
              <span class="diag-name">Скорость источника</span>
              <span class="diag-val">{{ speedKmh(e.diagnostics.pass.radialSpeedMps) }}</span>
            </div>
            <div class="diag-row">
              <span class="diag-name">Качество доплер-фита</span>
              <span class="diag-val">{{ pct(e.diagnostics.pass.dopplerQuality) }}</span>
            </div>
          </div>
        </template>

        <!-- Доплер: фит S-кривой + сырой тональный ряд (только пролёты с тон-треком) -->
        <template v-if="e.diagnostics?.doppler">
          <div class="det-section-title">
            Доплер · CPA-фит
            <span class="diag-tag">{{ e.diagnostics.doppler.sampleCount ?? 0 }} сэмплов в фите</span>
          </div>
          <div class="diag-grid">
            <div class="diag-row">
              <span class="diag-name">Центральная частота</span>
              <span class="diag-val">{{ freq(e.diagnostics.doppler.centerFreqHz) }}</span>
            </div>
            <div class="diag-row">
              <span class="diag-name">Полный размах свинга</span>
              <span class="diag-val">{{ dopplerSwingHz(e.diagnostics.doppler) }}</span>
            </div>
            <div class="diag-row">
              <span class="diag-name">Масштаб перехода τ</span>
              <span class="diag-val">{{ ms(e.diagnostics.doppler.tauMs) }}</span>
            </div>
            <div class="diag-row">
              <span class="diag-name">Остаток фита (RMS)</span>
              <span class="diag-val">{{ freq(e.diagnostics.doppler.residualHz) }}</span>
            </div>
          </div>
          <canvas
            :id="`tonal-cv-${keyOf(e)}`"
            class="diag-canvas"
            width="320"
            height="56"
          />
          <div class="diag-hint">
            тональный ряд: частота × время ({{ e.diagnostics.doppler.tonalSamples.length }} точек)
          </div>
        </template>

        <!-- Подавление: какие классы ещё в NMS-окне (почему окно могло быть подавлено) -->
        <template v-if="e.diagnostics?.suppression">
          <div class="det-section-title">
            Подавление · NMS
            <span class="diag-tag">рефрактерность по классам</span>
          </div>
          <div class="reason-chips">
            <span
              v-for="(val, cls) in e.diagnostics.suppression.refractoryRemainingMs"
              :key="cls"
              class="reason-chip"
            >{{ cls }}: {{ ms(val as number) }}</span>
          </div>
        </template>

        <div class="det-section-title">
          Аудио · 9 с (3 до + 3 после)
          <span
            v-if="onsetOffsetMs(e) !== null"
            class="onset-tag"
            title="смещение онсета внутри окна детекции"
          >онсет +{{ onsetOffsetMs(e) }} мс</span>
        </div>
        <audio
          v-if="audio[keyOf(e)]?.url"
          class="player"
          controls
          preload="none"
          :src="audio[keyOf(e)]!.url!"
        />
        <!-- Действия с аудио — только когда клип доступен (рядом с плеером) -->
        <div
          v-if="audio[keyOf(e)]?.url"
          class="audio-actions"
        >
          <button
            class="audio-btn"
            :disabled="savingWav.has(keyOf(e))"
            @click="downloadWav(e)"
          >
            {{ savingWav.has(keyOf(e)) ? '⏳ Кодирование…' : '⬇ Сохранить в WAV' }}
          </button>
          <button
            v-if="audioSentOk.has(e.id!) && !audioSendError.has(e.id!)"
            class="audio-btn ok"
            disabled
          >
            ✓ Отправлено
          </button>
          <button
            v-else
            class="audio-btn"
            :disabled="audioSending.has(e.id!)"
            @click="uploadAudio(e)"
          >
            {{ audioSending.has(e.id!) ? '⏳ Отправка…' : '↑ Отправить для анализа' }}
          </button>
          <button
            class="audio-btn debug"
            @click="openDebugGraphs(e)"
          >
            📊 Графики отладки
          </button>
        </div>
        <div
          v-if="audioSendError.has(e.id!)"
          class="audio-send-error"
        >
          ⚠ {{ audioSendError.get(e.id!) }}
        </div>
        <div
          v-else-if="audio[keyOf(e)]?.loaded"
          class="audio-missing"
        >
          🔇 Запись недоступна — хранится только для 5 последних детекций (до 1 ч).
        </div>
        <div
          v-else
          class="audio-missing"
        >
          ⏳ Загрузка записи…
        </div>
      </div>
    </div>
  </section>
</template>

<style scoped>
.feed {
  flex: 1 1 auto;
  /* min-height:0 lets this flex child shrink below its content so the list scrolls instead of
     overflowing the feed-section (tabs stay pinned above). */
  min-height: 0;
  overflow-y: auto;
  padding: 10px 12px 24px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  position: relative;
  z-index: 1;
}
.empty {
  text-align: center;
  color: #7a8499;
  font-size: 14px;
  padding: 40px 20px;
}
.card {
  width: 100%;
  text-align: left;
  background: #18202f;
  border: 1px solid #232d40;
  border-radius: 12px;
  padding: 10px 12px;
  color: inherit;
}
.card-row {
  display: flex;
  align-items: center;
  gap: 12px;
}
.cls-icon {
  font-size: 20px;
  line-height: 1;
  flex-shrink: 0;
  width: 24px;
  text-align: center;
  filter: drop-shadow(0 0 4px currentColor);
}
.card-body {
  flex: 1;
  min-width: 0;
}
.card-title {
  font-size: 15px;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 8px;
}
.card-conf {
  color: #2aabee;
  font-variant-numeric: tabular-nums;
}
.sync-icon {
  font-size: 13px;
  line-height: 1;
}
.card-time {
  font-size: 12px;
  color: #8b94a7;
  margin-top: 2px;
}
.info-btn,
.del-btn {
  flex-shrink: 0;
  width: 28px;
  height: 28px;
  border-radius: 8px;
  border: 1px solid #2a3346;
  background: #131b29;
  color: #8b94a7;
  font-size: 13px;
  cursor: pointer;
  line-height: 1;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 1px;
}
.info-btn .chev {
  font-size: 9px;
  transition: transform 0.15s;
}
.info-btn.open {
  background: rgba(42, 171, 238, 0.16);
  border-color: #2aabee;
  color: #cde9fb;
}
.info-btn.open .chev {
  transform: rotate(180deg);
}
.del-btn:active {
  background: #2a1414;
  color: #ff8a8a;
}

/* ── Детали ── */
.details {
  margin-top: 10px;
  padding-top: 10px;
  border-top: 1px solid #232d40;
}
.det-section-title {
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  color: #7a8499;
  margin: 8px 0 6px;
  display: flex;
  align-items: center;
  gap: 8px;
}
.det-section-title:first-child {
  margin-top: 0;
}
.onset-tag {
  margin-left: auto;
  text-transform: none;
  letter-spacing: 0;
  font-weight: 500;
  color: #8b94a7;
  font-variant-numeric: tabular-nums;
}
.stage-grid {
  display: flex;
  flex-direction: column;
  gap: 4px;
}
.stage-row {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
}
.stage-num {
  color: #2aabee;
  font-size: 14px;
  width: 16px;
  text-align: center;
}
.stage-name {
  color: #aeb8cc;
  flex: 1;
}
.stage-val {
  color: #e6e9ef;
  font-variant-numeric: tabular-nums;
  font-weight: 600;
}
.prob-grid {
  display: flex;
  flex-direction: column;
  gap: 5px;
}
.prob-row {
  display: flex;
  align-items: center;
  gap: 8px;
}
.prob-row.top {
  font-weight: 700;
}
.prob-icon {
  width: 20px;
  text-align: center;
  font-size: 14px;
  line-height: 1;
  flex-shrink: 0;
}
.prob-bar {
  flex: 1;
  height: 6px;
  border-radius: 3px;
  background: #232d40;
  overflow: hidden;
}
.prob-fill {
  display: block;
  height: 100%;
  border-radius: 3px;
  opacity: 0.85;
}
.prob-val {
  width: 38px;
  text-align: right;
  font-size: 12px;
  color: #aeb8cc;
  font-variant-numeric: tabular-nums;
}
.player {
  width: 100%;
  height: 34px;
  margin-top: 2px;
}
.audio-missing {
  font-size: 12px;
  color: #8b94a7;
  padding: 4px 0;
}
/* ── Действия с аудио (сохранить / отправить) ── */
.audio-actions {
  display: flex;
  gap: 8px;
  margin-top: 6px;
  flex-wrap: wrap;
}
.audio-btn {
  flex: 1 1 auto;
  padding: 5px 10px;
  border-radius: 7px;
  border: 1px solid #2a3346;
  background: #1a2333;
  color: #cde9fb;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;
}
.audio-btn:hover:not(:disabled) {
  border-color: #2aabee;
  background: rgba(42, 171, 238, 0.16);
}
.audio-btn:disabled {
  opacity: 0.55;
  cursor: default;
}
.audio-btn.ok {
  border-color: #1f3a24;
  background: #142117;
  color: #7fd69a;
  opacity: 1;
}
.audio-btn.debug {
  border-color: #3a2a46;
  background: #231a33;
  color: #c7b3e6;
}
.audio-btn.debug:hover:not(:disabled) {
  border-color: #9b59b6;
  background: rgba(155, 89, 182, 0.18);
}
.audio-send-error {
  margin-top: 4px;
  font-size: 11px;
  color: #ffb877;
}

/* ── Глубокая диагностика (новые секции «Подробнее») ── */
.diag-grid {
  display: flex;
  flex-direction: column;
  gap: 4px;
}
.diag-row {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
}
.diag-name {
  color: #aeb8cc;
  flex: 1;
}
.diag-val {
  color: #e6e9ef;
  font-variant-numeric: tabular-nums;
  font-weight: 600;
}
.diag-tag {
  margin-left: auto;
  text-transform: none;
  letter-spacing: 0;
  font-weight: 500;
  color: #8b94a7;
  font-size: 10px;
  font-variant-numeric: tabular-nums;
}
.diag-canvas {
  width: 100%;
  height: 56px;
  margin-top: 4px;
  border-radius: 6px;
  background: #131b29;
  border: 1px solid #232d40;
  display: block;
}
.diag-hint {
  margin-top: 3px;
  font-size: 11px;
  color: #8b94a7;
}
.reason-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  margin-top: 5px;
}
.reason-chip {
  font-size: 11px;
  padding: 2px 7px;
  border-radius: 10px;
  background: rgba(42, 171, 238, 0.14);
  color: #cde9fb;
  border: 1px solid rgba(42, 171, 238, 0.25);
  font-variant-numeric: tabular-nums;
}
.reason-chip.muted {
  background: #1a2333;
  color: #8b94a7;
  border-color: #2a3346;
}

/* ── Тег агрегации пролёта ── */
.agg-tag {
  margin-left: auto;
  text-transform: none;
  letter-spacing: 0;
  font-weight: 500;
  color: #8b94a7;
  font-size: 10px;
  font-variant-numeric: tabular-nums;
}

/* ── Статус отправки ── */
.sync-status {
  font-size: 12px;
  line-height: 1.4;
  padding: 6px 8px;
  border-radius: 8px;
  border: 1px solid #232d40;
  background: #131b29;
  color: #aeb8cc;
}
.sync-status.sent {
  border-color: #1f3a24;
  background: #142117;
  color: #7fd69a;
}
.sync-status.pending {
  color: #aeb8cc;
}
.sync-status.no_location,
.sync-status.error {
  border-color: #3a2a1c;
  background: #241a12;
  color: #ffb877;
}
.sync-status-head {
  font-weight: 600;
  color: inherit;
}
.sync-status-reason {
  margin-top: 3px;
  color: #cde9fb;
  word-break: break-word;
}
.sync-status-hint {
  margin-top: 4px;
  color: #8b94a7;
  font-size: 11px;
}
.sync-status-ok {
  margin-top: 4px;
  color: #7fd69a;
  font-size: 11px;
}
.retry-btn {
  margin-top: 8px;
  padding: 5px 10px;
  border-radius: 7px;
  border: 1px solid #2a3346;
  background: #1a2333;
  color: #cde9fb;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
}
.retry-btn:hover:not(:disabled) {
  border-color: #2aabee;
  background: rgba(42, 171, 238, 0.16);
}
.retry-btn:disabled {
  opacity: 0.55;
  cursor: default;
}
</style>
